import { type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { StatusBadge, type BadgeTone } from '@/components/dashboard/StatusBadge';
import { PillButton } from '@/components/entity/PillButton';
import { UnitDeleteButton } from '@/components/dashboard/units/UnitDeleteButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import {
  useDashboardPropertyDetail,
  useDashboardUnitLeaseContact,
} from '@/hooks/useDashboardProperties';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import {
  type UnitAvailabilityStatus,
  type UnitLeaseContact,
  type UnitSummary,
} from '@/lib/types/property';
import {
  formatFloorLevelLabel,
  formatMoveInTimingLabel,
  formatPropertyAddress,
  formatSquareFeet,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';

export function DashboardUnitDetail(): JSX.Element {
  const user = useDashboardUser();
  const { propertyId, unitId } = useParams<{ propertyId: string; unitId: string }>();
  const navigate = useNavigate();

  const normalizedPropertyId = (propertyId ?? '').trim();
  const normalizedUnitId = (unitId ?? '').trim();

  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: normalizedPropertyId.length > 0 ? normalizedPropertyId : null,
  });

  const backToProperty = () => {
    navigate(`/dashboard/properties/${normalizedPropertyId}`);
  };

  return (
    <section className="flex flex-col gap-xl py-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={backToProperty}
            className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            Back to property
          </button>
          <h1 className="text-3xl font-semibold text-text-primary">Unit detail</h1>
          <p className="text-sm text-text-primary/70">Review a single unit, or edit the record.</p>
        </div>
      </header>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={<UnitDetailSkeleton />}
        errorFallback={<UnitDetailError onBack={backToProperty} />}
      >
        {(detail) => {
          const unit = detail.units?.find((entry) => entry.id === normalizedUnitId) ?? null;

          if (!unit) {
            return (
              <div className="rounded-3xl border border-surface-subtle bg-white p-6 text-sm text-text-primary/80 shadow-sm">
                <p className="text-base font-semibold text-text-primary">Unit not found</p>
                <p className="mt-2">
                  The requested unit could not be located for this property. Return to the property
                  and pick another record.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <PillButton onClick={backToProperty}>Back to property</PillButton>
                </div>
              </div>
            );
          }

          const propertyLabel = formatPropertyAddress(detail.property.address);
          const divisibleLabel =
            unit.potentiallyDivisible &&
            typeof unit.minDivisibleSf === 'number' &&
            unit.minDivisibleSf > 0
              ? `Yes • Min ${formatSquareFeet(unit.minDivisibleSf)} SF`
              : unit.potentiallyDivisible
                ? 'Yes'
                : 'No';
          const incentives = typeof unit.incentives === 'string' ? unit.incentives.trim() : '';
          const hasIncentives = incentives.length > 0;

          return (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3 rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {propertyLabel}
                  </p>
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                    <h2 className="text-2xl font-semibold text-text-primary">
                      Unit {formatUnitLabel(unit)}
                    </h2>
                    <StatusBadge
                      tone={readinessTone(unit.availability.status)}
                      label={PUBLIC_READINESS_LABELS[unit.availability.status]}
                    />
                  </div>
                  <p className="text-sm text-text-primary/70">
                    Property code: {detail.property.propertyCode}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <PillButton
                    variant="secondary"
                    onClick={() => {
                      navigate(`/dashboard/properties/${unit.propertyId}`);
                    }}
                  >
                    Back to property
                  </PillButton>
                  <PillButton
                    onClick={() => {
                      navigate(
                        `/dashboard/properties/${normalizedPropertyId}/units/${normalizedUnitId}/edit`,
                      );
                    }}
                  >
                    Edit unit
                  </PillButton>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                  <div className="rounded-3xl border border-surface-subtle bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
                      Unit facts
                    </h3>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Fact label="Floor">{formatFloorLevelLabel(unit.floorLevel)}</Fact>
                      <Fact label="Area">{`${formatSquareFeet(unit.areaSf)} SF`}</Fact>
                      <Fact label="Divisible">{divisibleLabel}</Fact>
                      <Fact label="Move-in readiness">
                        {formatMoveInTimingLabel(unit.availabilityStart, unit.possessionTimeline)}
                      </Fact>
                      <Fact label="Rent">{formatRent(unit)}</Fact>
                      <Fact label="Availability status">
                        <StatusBadge
                          tone={readinessTone(unit.availability.status)}
                          label={PUBLIC_READINESS_LABELS[unit.availability.status]}
                        />
                      </Fact>
                      <Fact label="Display on public website?">
                        {unit.displayOnPublicWebsite ? 'Yes' : 'No'}
                      </Fact>
                      {hasIncentives ? <Fact label="Incentives">{incentives}</Fact> : null}
                    </dl>
                  </div>
                  <div className="rounded-3xl border border-surface-subtle bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
                      Contacts & settings
                    </h3>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Fact label="Contact email">{unit.contactEmail}</Fact>
                      <Fact label="Realtor outreach emails">{unit.published ? 'Yes' : 'No'}</Fact>
                    </dl>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="rounded-3xl border border-surface-subtle bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
                      Quick actions
                    </h3>
                    <div className="mt-4 flex flex-col gap-3">
                      <PillButton
                        className="w-full"
                        onClick={() => {
                          navigate(
                            `/dashboard/properties/${normalizedPropertyId}/units/${normalizedUnitId}/edit`,
                          );
                        }}
                      >
                        Edit unit
                      </PillButton>
                      <UnitDeleteButton
                        propertyId={unit.propertyId}
                        unit={unit}
                        variant="secondary"
                        fullWidth
                        onDeleted={backToProperty}
                      />
                      <PillButton
                        variant="secondary"
                        className="w-full"
                        onClick={() => {
                          navigate(`/dashboard/properties/${unit.propertyId}`);
                        }}
                      >
                        Back to property
                      </PillButton>
                    </div>
                  </div>
                  {unit.availability.status === 'leased' ? (
                    <LeaseholderContactCard unitId={unit.id} user={user} />
                  ) : null}
                </div>
              </div>
            </div>
          );
        }}
      </AsyncState>
    </section>
  );
}

function LeaseholderContactCard({
  unitId,
  user,
}: {
  unitId: string;
  user: ReturnType<typeof useDashboardUser>;
}): JSX.Element {
  const contactQuery = useDashboardUnitLeaseContact({ user, unitId });
  const contact = contactQuery.data ?? null;

  return (
    <div className="rounded-3xl border border-brand-primary/20 bg-brand-primary/5 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Leaseholder contact
          </h3>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-primary">
            Private admin-only
          </p>
        </div>
      </div>
      {contactQuery.isLoading || contactQuery.isFetching ? (
        <p className="mt-4 text-sm text-text-primary/70">Loading private contact…</p>
      ) : contact === null ? (
        <p className="mt-4 text-sm text-text-primary/70">
          No private leaseholder contact is saved for this leased unit.
        </p>
      ) : (
        <LeaseholderContactDetails contact={contact} />
      )}
    </div>
  );
}

function LeaseholderContactDetails({ contact }: { contact: UnitLeaseContact }): JSX.Element {
  const hasPhone =
    typeof contact.officePhone === 'string' ||
    typeof contact.cellPhone === 'string' ||
    typeof contact.homePhone === 'string';

  return (
    <div className="mt-4 flex flex-col gap-4 text-sm text-text-primary">
      <div>
        <p className="text-lg font-semibold">{contact.tenantLabel}</p>
        {typeof contact.contactName === 'string' ? (
          <p className="text-text-primary/70">{contact.contactName}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {typeof contact.primaryEmail === 'string' ? (
          <a
            href={`mailto:${contact.primaryEmail}`}
            className="min-h-11 rounded-full bg-brand-primary px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-brand-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            Email
          </a>
        ) : null}
        {renderPhoneAction('Call office', contact.officeTelHref)}
        {renderPhoneAction('Call cell', contact.cellTelHref)}
        {renderPhoneAction('Call home', contact.homeTelHref)}
      </div>
      <dl className="grid gap-3">
        {typeof contact.primaryEmail === 'string' ? (
          <ContactFact label="Email">{contact.primaryEmail}</ContactFact>
        ) : null}
        {typeof contact.officePhone === 'string' ? (
          <ContactFact label="Office">{contact.officePhone}</ContactFact>
        ) : null}
        {typeof contact.cellPhone === 'string' ? (
          <ContactFact label="Cell">{contact.cellPhone}</ContactFact>
        ) : null}
        {typeof contact.homePhone === 'string' ? (
          <ContactFact label="Home">{contact.homePhone}</ContactFact>
        ) : null}
        {typeof contact.faxPhone === 'string' ? (
          <ContactFact label="Fax">{contact.faxPhone}</ContactFact>
        ) : null}
        {hasPhone ? null : <ContactFact label="Phone">Not listed</ContactFact>}
      </dl>
    </div>
  );
}

function renderPhoneAction(label: string, href: string | null): JSX.Element | null {
  if (typeof href !== 'string') {
    return null;
  }

  return (
    <a
      href={href}
      className="min-h-11 rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
    >
      {label}
    </a>
  );
}

function ContactFact({ label, children }: { label: string; children: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs uppercase tracking-wide text-text-primary/60">{label}</dt>
      <dd className="font-semibold text-text-primary">{children}</dd>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: JSX.Element | string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs uppercase tracking-wide text-text-primary/60">{label}</dt>
      <dd className="text-sm font-semibold text-text-primary">{children}</dd>
    </div>
  );
}

function UnitDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="bg-surface-muted h-56 animate-pulse rounded-3xl border border-surface-subtle" />
          <div className="bg-surface-muted h-40 animate-pulse rounded-3xl border border-surface-subtle" />
        </div>
        <div className="space-y-4">
          <div className="bg-surface-muted h-40 animate-pulse rounded-3xl border border-surface-subtle" />
        </div>
      </div>
    </div>
  );
}

function UnitDetailError({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <div className="border-destructive/30 bg-destructive/5 rounded-3xl border p-6 text-sm text-text-primary shadow-sm">
      <p className="text-destructive text-base font-semibold">Unable to load unit</p>
      <p className="mt-2 text-text-primary/80">
        We could not load this unit. Return to the property and try again.
      </p>
      <div className="mt-4">
        <PillButton onClick={onBack}>Back to property</PillButton>
      </div>
    </div>
  );
}

function formatRent(unit: UnitSummary): string {
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  });
  const suffix = 'SF Annual';
  const prefix = formatter.format(unit.rent.netRentPerSf);
  if (unit.rent.rentBasis === 'gross') {
    return `${prefix}/${suffix}`;
  }
  const addl =
    typeof unit.rent.additionalRentPerSf === 'number'
      ? ` + ${formatter.format(unit.rent.additionalRentPerSf)}/${suffix}`
      : '';
  return `${prefix}/${suffix}${addl}`;
}

function readinessTone(status: UnitAvailabilityStatus): BadgeTone {
  switch (status) {
    case 'available':
      return 'success';
    case 'unavailable':
      return 'warning';
    default:
      return 'neutral';
  }
}
